import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { CALENDAR_VIEW } from '../labels'
import type { SourceAvailability, SourceCell } from '../lib/types'
import {
  ACCENT,
  BAND_SOFT,
  FONT_MONO,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  LINE_SOFT,
  PAPER,
} from '../theme'

interface Props {
  fichas: SourceAvailability[]
  byKey: Map<string, SourceCell>
  onSelect: (cell: SourceCell) => void
}

/** Cuántas horas de silencio bastan para dudar de que alguien fue a ver. */
const REVISION_RANCIA_H = 36

/**
 * El calendario leído como ficha, una por fuente.
 *
 * Es la vista por defecto porque responde la pregunta que de verdad se hace a
 * diario —*esta fuente, ¿hasta cuándo está en cada producto y vamos tarde?*— sin
 * obligar a reconstruirla de puntos de colores mes por mes.
 *
 * Dos decisiones de lectura:
 *
 * * **Un producto que no aplica no lleva renglón.** Un "—" diría "no sé"; la
 *   ausencia dice "esta fuente no alimenta eso", que es lo cierto.
 * * **La última revisión se muestra sólo cuando está rancia.** En operación
 *   normal es ruido; cuando el poller se cae es lo único que distingue "no ha
 *   llegado" de "nadie ha ido a ver", y entonces tiene que saltar.
 */
export function SourceCards({ fichas, byKey, onSelect }: Props) {
  const ahora = Date.now()

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 0,
        borderTop: `1px solid ${LINE_SOFT}`,
      }}
    >
      {fichas.map((f) => (
        <Ficha key={f.source_id} ficha={f} ahora={ahora} byKey={byKey} onSelect={onSelect} />
      ))}
    </Box>
  )
}

function Ficha({
  ficha,
  ahora,
  byKey,
  onSelect,
}: {
  ficha: SourceAvailability
  ahora: number
  byKey: Map<string, SourceCell>
  onSelect: (cell: SourceCell) => void
}) {
  // Al tocar la ficha se abre el cajón de la fuente en el mes más relevante: el
  // que va tarde si lo hay, y si no el último que llegó.
  const foco = ficha.periodo_pendiente ?? ficha.sharepoint_hasta
  const celda = foco ? byKey.get(`${ficha.source_id}|${foco}`) : undefined

  const rancia = esRancia(ficha.ultima_revision, ahora)

  return (
    <ButtonBase
      onClick={() => celda && onSelect(celda)}
      disabled={!celda}
      sx={{
        display: 'block',
        textAlign: 'left',
        width: '100%',
        px: 3,
        py: 2.25,
        borderBottom: `1px solid ${LINE_SOFT}`,
        borderRight: { md: `1px solid ${LINE_SOFT}` },
        bgcolor: PAPER,
        '&:hover': { bgcolor: celda ? BAND_SOFT : PAPER },
      }}
    >
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
        <Typography
          sx={{
            fontFamily: FONT_MONO,
            fontSize: '0.8125rem',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: INK,
          }}
        >
          {ficha.source_name}
        </Typography>
        {ficha.deadline_superado && (
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: ACCENT,
              flexShrink: 0,
            }}
          />
        )}
      </Stack>

      <Stack spacing={0.35}>
        {ficha.productos.map((p) => (
          <Renglon
            key={p.cycle_id}
            etiqueta={CALENDAR_VIEW.textInProduct[p.clave] ?? p.cycle_name}
            valor={p.al_dia_label ?? CALENDAR_VIEW.textNothing}
            apagado={!p.al_dia_label}
          />
        ))}

        {ficha.productos.length === 0 && (
          <Typography
            sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT, py: 0.25 }}
          >
            {CALENDAR_VIEW.textNoProducts}
          </Typography>
        )}

        <Renglon
          etiqueta={CALENDAR_VIEW.textSharepoint}
          valor={ficha.sharepoint_label ?? CALENDAR_VIEW.textNothing}
          apagado={!ficha.sharepoint_label}
        />

        <Renglon
          etiqueta={CALENDAR_VIEW.textDeadline}
          valor={
            ficha.deadline_superado
              ? `${CALENDAR_VIEW.textYes} · ${CALENDAR_VIEW.textOverdueSince(
                  ficha.periodo_pendiente_label ?? '',
                  ficha.dias_tarde,
                )}`
              : CALENDAR_VIEW.textNo
          }
          alerta={ficha.deadline_superado}
        />

        {rancia && (
          <Renglon
            etiqueta={CALENDAR_VIEW.textLastChecked}
            valor={ficha.ultima_revision ? hace(ficha.ultima_revision, ahora) : CALENDAR_VIEW.textNeverChecked}
            alerta
          />
        )}
      </Stack>
    </ButtonBase>
  )
}

function Renglon({
  etiqueta,
  valor,
  alerta = false,
  apagado = false,
}: {
  etiqueta: string
  valor: string
  alerta?: boolean
  apagado?: boolean
}) {
  return (
    <Stack direction="row" alignItems="baseline" spacing={1}>
      <Typography
        sx={{
          fontFamily: FONT_MONO,
          fontSize: '0.6875rem',
          color: INK_SOFT,
          flexShrink: 0,
          minWidth: 168,
        }}
      >
        {etiqueta}
      </Typography>
      <Box sx={{ flex: 1, borderBottom: `1px dotted ${LINE}`, mb: '3px' }} />
      <Typography
        sx={{
          fontFamily: FONT_MONO,
          fontSize: '0.6875rem',
          fontWeight: alerta ? 600 : 500,
          color: alerta ? ACCENT : apagado ? INK_FAINT : INK,
          flexShrink: 0,
          textAlign: 'right',
        }}
      >
        {valor}
      </Typography>
    </Stack>
  )
}

/** Null cuenta como rancia: nunca revisada es peor que revisada hace mucho. */
function esRancia(iso: string | null, ahora: number): boolean {
  if (!iso) return true
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return true
  return ahora - t > REVISION_RANCIA_H * 3600 * 1000
}

function hace(iso: string, ahora: number): string {
  const horas = Math.floor((ahora - Date.parse(iso)) / 3600000)
  if (horas < 48) return `hace ${horas} h`
  return `hace ${Math.floor(horas / 24)} días`
}
